/**
 * 既存メール本文をペーストして Claude に AI 解析させ、
 * 件名 + セクション構造に分解する取り込みモーダル。
 * 解析結果を MailGeneratePage の生成結果プレビューに流し込む。
 */
import { httpsCallable } from 'firebase/functions';
import { FileInput, Sparkles } from 'lucide-react';
import { useState } from 'react';

import type { MailSection } from '@goenhub/shared';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { functions } from '@/lib/firebase';

interface ImportedMail {
  subject: string;
  sections: MailSection[];
  overview: string;
}

interface ImportResponse {
  ok: boolean;
  modelUsed: string;
  imported: ImportedMail;
}

const importFn = httpsCallable<{ rawText: string }, ImportResponse>(
  functions,
  'importMailFromText',
);

interface ImportMailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 解析結果を呼び出し側に渡す */
  onImported: (imported: ImportedMail, modelUsed: string) => void;
}

export function ImportMailDialog({ open, onOpenChange, onImported }: ImportMailDialogProps) {
  const [rawText, setRawText] = useState('');
  const [analyzing, setAnalyzing] = useState(false);

  const handleAnalyze = async () => {
    if (!rawText.trim()) {
      toast.error('メール本文を入力してください');
      return;
    }
    if (rawText.length > 20000) {
      toast.error('20000 文字以内にしてください');
      return;
    }
    setAnalyzing(true);
    try {
      const res = await importFn({ rawText });
      toast.success(
        `解析完了: 件名 + ${res.data.imported.sections.length} セクション (${res.data.modelUsed})`,
      );
      onImported(res.data.imported, res.data.modelUsed);
      setRawText('');
      onOpenChange(false);
    } catch (err) {
      toast.error(`解析に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileInput className="size-5" />
            既存メールを取り込む
          </DialogTitle>
          <DialogDescription>
            過去のメール本文を貼り付けると、Claude が件名とセクション構造を自動抽出して下書きにします。
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 flex flex-col gap-2 min-h-0">
          <Label htmlFor="import-text">メール本文（件名行を含めても可）</Label>
          <Textarea
            id="import-text"
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            placeholder={`例:\n件名: 春の新商品のご案内\n\nお客様各位\n\n平素より...\n\n今回は新商品「○○」をご案内いたします。...`}
            rows={14}
            className="flex-1 min-h-[200px] resize-none font-mono text-[12px]"
            disabled={analyzing}
          />
          <div className="flex items-center justify-between text-xs text-[var(--text-muted)]">
            <span>{rawText.length.toLocaleString()} / 20,000 文字</span>
            <span className="text-[11px]">Claude が解析（10〜30 秒）</span>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={analyzing}
          >
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleAnalyze}
            disabled={analyzing || !rawText.trim()}
          >
            {analyzing ? (
              <>
                <Spinner className="size-4" />
                解析中...
              </>
            ) : (
              <>
                <Sparkles />
                解析して下書きにする
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
